import chunk from "lodash-es/chunk";
import isUndefined from "lodash-es/isUndefined";
import { makeAutoObservable, runInAction } from "mobx";
import * as z from "zod";
import { apiClient } from "../common/apiClient";
import { ServerNotFoundError, UserCancelError } from "../common/errors";
import { logger } from "../common/logger";
import { platformRoutes, resolvePlatformRequestUrl } from "../common/platformRoutes";
import type { BaseStoreType } from "../common/types/BaseStoreType";
import type { Progress } from "../common/types/commonTypes";
import type {
   AvailabilityWithRequestedQuantity,
   CartItem,
   Product,
   ProductAndAvailability,
   ProductAvailability,
   Unavailable
} from "../common/types/productTypes";
import { accumulate } from "../common/utils";
import { formatM3Date } from "../common/utils/dateUtils";
import { ZProductAvailabilities } from "../common/zodSchemas/product";
import theme from "../themes/theme";
import authStore from "./auth/authStore";
import { startSubmitAvailabilityTracking } from "./cart/cartAvailabilityExperiment";
import cartStore from "./cart/cartStore";
import deliveryDatesStore from "./deliveryDates/deliveryDatesStore";
import productStore from "./product/productStore";

type CartItemsBySku = { [sku: string]: CartItem };

type QuantitiesBySku = { [sku: string]: number };

export type AvailabilityCheckResult = {
   allAvailable: boolean;
   checked: boolean;
};

type AvailabilityStoreType = BaseStoreType & {
   newCartItems: CartItemsBySku;
   unavailable: Unavailable[] | null;
   alternatives: ProductAndAvailability[];
   availabilityFetchProgress: Progress | null;
   lookingForAlternatives: boolean;
   isUnavailable: (sku: string) => boolean;
   hasUnavailable: () => boolean;
   abortController?: AbortController;
   checkAvailability: () => Promise<AvailabilityCheckResult>;
   fetchAvailabilitiesBySkus: (skus: string[]) => Promise<AvailabilityWithRequestedQuantity[]>;
   fetchAlternatives: () => Promise<void>;

   clearAvailability: () => void;
};

const DEFAULT_AVAILABILITY_CHECK_QTY = 1000;
const AVAILABILITY_CHUNK_SIZE = 10;

const chunkQuantitiesBySku = (quantities: QuantitiesBySku, chunkSize: number): QuantitiesBySku[] => {
   const quantitiesArray = Object.keys(quantities).map((sku) => ({ sku, qty: quantities[sku] }));
   if (quantitiesArray.length > chunkSize) {
      return chunk(quantitiesArray, chunkSize).map((chunk) =>
         chunk.map((item) => ({ [item.sku]: item.qty })).reduce(accumulate, {})
      );
   }
   return [quantities];
};

const serialFetchAvailability = async (chunks: QuantitiesBySku[]): Promise<ProductAvailability[]> => {
   if (chunks.length) {
      const firstChunkAvailability = await fetchAvailability(chunks[0]);
      const subsequentChunksAvailability = await serialFetchAvailability(chunks.slice(1));
      return [...firstChunkAvailability, ...subsequentChunksAvailability];
   }
   return [];
};

const fetchAvailability = async (quantities: QuantitiesBySku): Promise<ProductAvailability[]> => {
   const skus = Object.keys(quantities);
   if (!skus.length) {
      return [];
   }

   if (skus.length > AVAILABILITY_CHUNK_SIZE) {
      const chunks: QuantitiesBySku[] = chunkQuantitiesBySku(quantities, AVAILABILITY_CHUNK_SIZE);
      runInAction(() => {
         availabilityStore.availabilityFetchProgress = {
            count: 0,
            target: chunks.length
         };
      });

      try {
         const fetches: ProductAvailability[][] = [await serialFetchAvailability(chunks)];

         runInAction(() => {
            availabilityStore.abortController = undefined;
         });

         const availabilities: ProductAvailability[] = fetches.flat();
         return availabilities;
      } catch (err) {
         if ((err as { name?: string })?.name === "AbortError") {
            throw new UserCancelError();
         }
         throw err;
      }
   } else if (!availabilityStore.availabilityFetchProgress) {
      runInAction(() => {
         availabilityStore.availabilityFetchProgress = {
            count: 0,
            target: 1
         };
      });
   }
   const abortController: AbortController = availabilityStore.abortController || new AbortController();
   runInAction(() => {
      availabilityStore.abortController = abortController;
   });

   const params = {
      customerNumber: authStore.currentCompany,
      m3OrderType: cartStore.orderType,
      requestedDeliveryDate: formatM3Date(deliveryDatesStore.getCurrentDelivery()?.date),
      products: skus.map((sku) => `${sku}*${quantities[sku]}`).join("|")
   };
   const throwAvailabilityControlUnavailable = () => {
      logger.log("order", "availability control unavailable");
      throw new ServerNotFoundError();
   };
   try {
      const productAvailabilityUrl = resolvePlatformRequestUrl(platformRoutes.productAvailability, { storeId: theme.storeId });
      const availability: z.infer<typeof ZProductAvailabilities> = await apiClient(
         productAvailabilityUrl,
         authStore.getSessionToken()
      )
         .signal(abortController)
         .query(params)
         .get()
         .error(500, throwAvailabilityControlUnavailable)
         .error(501, throwAvailabilityControlUnavailable)
         .error(502, throwAvailabilityControlUnavailable)
         .error(503, throwAvailabilityControlUnavailable)
         .error(504, throwAvailabilityControlUnavailable)
         .fetchError(throwAvailabilityControlUnavailable)
         .json();
      const validation = z.safeParse(ZProductAvailabilities, availability);
      if (validation.success) {
         logger.log("products", "successful validation of availability", availability);
      } else {
         logger.error("products", "validation failed for availability", validation, availability);
      }

      runInAction(() => {
         if (availabilityStore.availabilityFetchProgress) {
            const { count, target } = availabilityStore.availabilityFetchProgress;
            const newCount = count + 1;
            if (newCount >= target) {
               availabilityStore.abortController = undefined;
            }

            availabilityStore.availabilityFetchProgress = {
               target,
               count: newCount > target ? target : newCount
            };
         } else {
            availabilityStore.abortController = undefined;
         }
      });

      return availability;
   } catch (err) {
      runInAction(() => {
         availabilityStore.availabilityFetchProgress = null;
         availabilityStore.abortController = undefined;
      });

      throw err;
   }
};

const availabilityStore: AvailabilityStoreType = makeAutoObservable({
   newCartItems: {},
   unavailable: null,
   lookingForAlternatives: false,
   alternatives: [],
   availabilityFetchProgress: null,

   hasUnavailable: () => {
      return availabilityStore.unavailable !== null && !!availabilityStore.unavailable.length;
   },

   isUnavailable: (sku: string) => {
      const { unavailable: availability } = availabilityStore;
      if (!availability) {
         return false;
      }
      return availability.some((a) => a.productNumber === sku && !a.available);
   },
   clearAvailability: () => {
      availabilityStore.newCartItems = {};
      availabilityStore.unavailable = null;
      availabilityStore.alternatives = [];
   },

   fetchAvailabilitiesBySkus: async (skus: string[]): Promise<AvailabilityWithRequestedQuantity[]> => {
      if (!skus.length) {
         return [];
      }
      const quantities: QuantitiesBySku = skus.map((sku) => ({ [sku]: DEFAULT_AVAILABILITY_CHECK_QTY })).reduce(accumulate, {});
      return (await fetchAvailability(quantities)).map((availability) => ({
         requestedQuantity: quantities[availability.productNumber] || DEFAULT_AVAILABILITY_CHECK_QTY,
         ...availability
      }));
   },

   checkAvailability: async (): Promise<AvailabilityCheckResult> => {
      if (!theme.productAvailability.enabled) {
         return { allAvailable: true, checked: false };
      }
      const currentDelivery = deliveryDatesStore.getCurrentDelivery();
      if (!currentDelivery) {
         availabilityStore.clearAvailability();
         return { allAvailable: true, checked: false };
      }

      const quantities: QuantitiesBySku = cartStore.editing
         ? cartStore
              .getEditingCartChanges()
              .filter((delta) => delta.qtyDelta > 0)
              .map((delta) => ({ [delta.sku]: delta.qtyDelta }))
              .reduce(accumulate, {})
         : cartStore.items.map((item) => ({ [item.sku]: item.qty })).reduce(accumulate, {});

      const trackSubmitAvailability = startSubmitAvailabilityTracking();
      const availabilities = await fetchAvailability(quantities);
      trackSubmitAvailability(quantities, availabilities);
      // A batch can succeed with a partial or empty response, so require a result for every requested SKU
      const checked = Object.keys(quantities).every((sku) =>
         availabilities.some((availability) => availability.productNumber === sku)
      );

      const unavailable = availabilities
         .filter((availability) => !availability.available)
         .flatMap((availability) => {
            const cartItem = cartStore.getCartItem(availability.productNumber);
            if (availability.delayedDeliveryDate) {
               availability.availableQuantity = 0;
            }

            return cartItem
               ? ({
                    ...availability,
                    selectedQuantity: availability.availableQuantity,
                    requestedQuantity: cartItem.qty,
                    available: false
                 } as Unavailable)
               : ([] as Unavailable[]);
         });

      if (!unavailable.length) {
         availabilityStore.clearAvailability();
         return { allAvailable: true, checked };
      }

      availabilityStore.unavailable = unavailable;
      void availabilityStore.fetchAlternatives();
      return { allAvailable: false, checked };
   },

   fetchAlternatives: async () => {
      if (!availabilityStore.unavailable) {
         return;
      }
      availabilityStore.lookingForAlternatives = true;

      const unavailableProducts = availabilityStore.unavailable
         .map((availability) => productStore.resolveSku(availability.productNumber))
         .filter((product): product is Product => !isUndefined(product));

      const alternativeSkus = unavailableProducts
         .flatMap((product) => product.similarProducts || [])
         .filter((sku, i, skus) => skus.indexOf(sku) === i)
         .filter((sku) => !unavailableProducts.find((product) => product.sku === sku));

      try {
         const alternativeAvailabilities = (await availabilityStore.fetchAvailabilitiesBySkus(alternativeSkus)).map(
            (availability) => {
               if (availability.delayedDeliveryDate) {
                  return { ...availability, availableQuantity: 0 };
               }
               return availability;
            }
         );

         const alternativeProductAvailabilities = alternativeSkus
            .map((sku) => ({
               product: productStore.resolveSku(sku, "buyable"),
               availability: alternativeAvailabilities.find((availability) => availability.productNumber === sku)
            }))
            .filter(
               (productAndAvailability): productAndAvailability is ProductAndAvailability =>
                  !isUndefined(productAndAvailability.product) && !isUndefined(productAndAvailability.availability)
            );

         const productsWithoutAvailableAlternatives = unavailableProducts.filter((product) => {
            return product.similarProducts
               .filter((sku) => !unavailableProducts.find((unavailableProduct) => unavailableProduct.sku === sku))
               .every((similarSku) => {
                  const productAvailability = alternativeAvailabilities.find(
                     (availability) => availability.productNumber === similarSku
                  );
                  return !productAvailability?.availableQuantity;
               });
         });

         const alternativeProductAndAvailabilities = alternativeProductAvailabilities.filter(
            (productAndAvailability) => productAndAvailability.availability.availableQuantity
         );

         for (const product of productsWithoutAvailableAlternatives) {
            const alternativeProducts = product.similarProducts
               .map((sku) => productStore.resolveSku(sku, "inAssortment"))
               .flatMap((product) => (product ? product : []));
            for (const alternativeProduct of alternativeProducts) {
               if (
                  !alternativeProductAndAvailabilities.find(
                     (productAndAvailability) => productAndAvailability.product.sku === alternativeProduct.sku
                  )
               ) {
                  const availability = alternativeAvailabilities.find(
                     (availability) => availability.productNumber === alternativeProduct.sku
                  );
                  if (availability) {
                     alternativeProductAndAvailabilities.push({
                        product: alternativeProduct,
                        availability: { selectedQuantity: 0, ...availability }
                     });
                  }
               }
            }
         }

         availabilityStore.alternatives = alternativeProductAndAvailabilities.filter(
            (productAndAvailability) =>
               !availabilityStore.unavailable?.find(
                  (availability) => availability.productNumber === productAndAvailability.product.sku
               ) && productAndAvailability.availability.available
         );
      } catch (_err) {
         availabilityStore.clearAvailability();
      } finally {
         availabilityStore.lookingForAlternatives = false;
      }
   },
   clearCompanySpecificData: () => {
      availabilityStore.clearAvailability();
   }
} satisfies AvailabilityStoreType);

export default availabilityStore;
